import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, getActiveTenantId, setActiveTenantId } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [activeTenantId, setActiveTenant] = useState(getActiveTenantId());

  const ensureTenant = useCallback((data) => {
    const tenants = data?.tenants || [];
    const current = getActiveTenantId();
    const valid = tenants.find((t) => t.id === current);
    const next = valid ? current : (tenants.find((t) => t.is_default) || tenants[0])?.id || "";
    setActiveTenantId(next);
    setActiveTenant(next);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      ensureTenant(data);
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, [ensureTenant]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const switchTenant = useCallback(async (tenantId) => {
    setActiveTenantId(tenantId);
    setActiveTenant(tenantId);
    await fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        return { ok: false, requires2fa: true, tmpToken: data.tmp_token };
      }
      const me = await fetchMe();
      return { ok: true, user: me };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verify2fa = async (tmpToken, code) => {
    try {
      await api.post("/auth/login/2fa", { tmp_token: tmpToken, code });
      const me = await fetchMe();
      return { ok: true, user: me };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      await api.post("/auth/register", payload);
      const me = await fetchMe();
      return { ok: true, user: me };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setActiveTenantId("");
    setActiveTenant("");
    setUser(false);
  };

  const activeTenant = (user?.tenants || []).find((t) => t.id === activeTenantId) || null;

  return (
    <AuthContext.Provider
      value={{ user, login, verify2fa, register, logout, refresh: fetchMe, activeTenantId, activeTenant, switchTenant }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
